import './note-list-filter.styles.scss'

import { useEffect } from 'react'

import { ApiNote } from '../../../../../../api/_legacy/generated-api'
import {
  DateDisplay,
  LinkButton,
  ResultTable,
  Space,
  TextInput
} from '../../../../../../components/core-v2'
import { useUser } from '../../../../../../utilities/hooks/useUser'
import { useAppSelector } from '../../../../store/hooks'
import { useActions } from '../../../../store/use-actions'
import { unpascal } from '../../../../util'
import { getUserDisplayInfo, isCollateralUser } from '../../../../util/user'
import { actions as rawActions } from './note-search-slice'

type NoteSearchProps = {
  onClickNote: (note: ApiNote) => void
}

export function NoteSearch({ onClickNote }: NoteSearchProps) {
  const state = useAppSelector(state => state.noteSearch)
  const actions = useActions(rawActions)

  const { data: user } = useUser()
  const userInfo = getUserDisplayInfo(user)
  const { userRoles, userType } = userInfo
  const collateralUser = isCollateralUser(userRoles)

  useEffect(() => {
    actions.init()
  }, [actions])

  return (
    <Space mx={5} my={6}>
      <section className="note-filter-block">
        <div className="search">
          {userType !== 'business-unit' && (
            <div className="filters">
              <div className="clients">
                <label htmlFor="note-filter-block__client">Client</label>
                <select
                  id="note-filter-block__client"
                  value={state.selectedClientId}
                  onChange={e => actions.setClient(e.target.value)}
                >
                  <option value="">All Clients</option>
                  {state.clients?.map(c => (
                    <option key={c.clientId} value={c.clientId}>
                      {c.clientName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="business-units">
                <label htmlFor="note-filter-business-units">Business Unit</label>
                <select
                  id="note-filter-business-units"
                  value={state.selectedBusinessUnitId}
                  onChange={e => actions.setBusinessUnit(e.target.value)}
                >
                  <option value="">All Business Units</option>
                  {state.currentBusinessUnits?.map(bu => (
                    <option key={bu.businessUnitId} value={bu.businessUnitId}>
                      {bu.businessUnitName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <TextInput
            value={state.searchTerm}
            onChange={e => actions.setSearchTerm(e.target.value)}
          />
        </div>
        <div className="table-wrapper">
          <ResultTable
            columns={[
              {
                title: 'Name',
                name: 'noteName',
                renderHeader: () => <>{state.filteredNotes.length} Notes</>,
                render: item => {
                  const note = state.notes.find(n => n.noteName === item.noteName)

                  if (note.isCollateralForLoan && !collateralUser) {
                    return item.noteName
                  }

                  return (
                    <LinkButton
                      data-testid="noteName"
                      underline={false}
                      onClick={() => onClickNote(note)}
                    >
                      {item.noteName}
                    </LinkButton>
                  )
                }
              },
              {
                title: 'Note Type',
                name: 'taxClassification',
                render: item => <>{unpascal(item.taxClassification)}</>
              },
              {
                title: 'Dividend',
                name: 'dividendProcessing'
              },
              {
                title: 'Start Date',
                name: 'startDate',
                render: item => <DateDisplay value={item.startDate} />
              }
            ]}
            data={state.filteredNotes}
            highlightRow={row =>
              row.isCollateralForLoan && !collateralUser ? 'highlight-row' : ''
            }
          />
        </div>
      </section>
    </Space>
  )
}
